import React, { useCallback, useEffect, useState } from "react";

import { TVideoCardFeatureV2 } from "pages/LayoutComponents/VideoCardGrid/VideoCardGrid.types";
import { useRef } from "react";
import * as S from "./InstallationAddonsCarousel.styles";
import { VideoCardTags } from "./InstallationAddonTags";
import { useTranslation } from "hooks/useTranslation";
import { buildLearnMoreRoute } from "helpers/route";
import { ArrowRightPrimaryIcon } from "@ubnt/icons";
import { useSiteSettings } from "context/SiteSettingsContext";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";
import { DimensionsBox } from "./DimensionsBox";
import { ProgressiveBlurGradient } from "components/ProgressiveBlurGradient";
import { getPath } from "util/route";
import { useVideoCleanup } from "hooks/useDestroyVideoRef";

export const InstallationAddonsCarousel = ({
  items,
  isInView,
}: Readonly<{ items: Array<TVideoCardFeatureV2>; isInView?: boolean }>) => {
  const { t } = useTranslation();
  const { routes } = useSiteSettings();

  const videoRef = useRef({});
  useVideoCleanup(videoRef);

  const [activeSlide, setActiveSlide] = useState(items[0]);
  const [hasAnimatedOnce, setHasAnimatedOnce] = useState(false);
  const [hasUserInteraction, setHasUserInteraction] = useState(false);
  const [isHoveringTabs, setIsHoveringTabs] = useState(false);

  const handleTabClick = useCallback((item: TVideoCardFeatureV2) => {
    setHasAnimatedOnce(true);
    setHasUserInteraction(true);
    setIsHoveringTabs(false);
    setActiveSlide(item);
  }, []);

  const getLearnMoreLink = useCallback(
    (item: TVideoCardFeatureV2) => {
      if (!item.learnMoreLink && !item.uiComLink) {
        return null;
      }

      if (item.uiComLink) {
        return {
          href: getPath(routes, item.uiComLink.href),
          label: t(item.uiComLink.label),
        };
      }

      return {
        href: buildLearnMoreRoute(routes, item.learnMoreLink),
        label: t("commons.viewInTechSpecs"),
      };
    },
    [routes, t],
  );

  const getNextSlide = useCallback(
    (index: number) => {
      const nextIndex = index + 1;
      const isLastIndex = nextIndex >= items.length;
      const correctedIndex = isLastIndex ? 0 : nextIndex;

      return items[correctedIndex];
    },
    [items],
  );

  const autoSwitchNextSlide = useCallback(
    (item: TVideoCardFeatureV2, index: number) => {
      const hasAnyUserInteraction =
        !isInView || hasUserInteraction || isHoveringTabs;

      if (activeSlide?.id !== item.id || hasAnyUserInteraction) {
        return;
      }

      const nextSlide = getNextSlide(index);

      setActiveSlide(nextSlide);
    },
    [activeSlide, getNextSlide, hasUserInteraction, isInView, isHoveringTabs],
  );

  const restartSlideAutoplay = useCallback(() => {
    if (hasUserInteraction) {
      return;
    }

    setIsHoveringTabs(false);
    const index = items.findIndex(({ id }) => id === activeSlide?.id);

    if (!!videoRef.current?.[index]?.ended) {
      const nextSlide = getNextSlide(index);
      setActiveSlide(nextSlide);
    }
  }, [activeSlide, getNextSlide, hasUserInteraction, items]);

  useEffect(() => {
    const index = items.findIndex(({ id }) => id === activeSlide.id);
    const ref = videoRef.current[index];

    if (isInView && videoRef.current[index]) {
      try {
        ref.currentTime = 0;
        ref.play();
      } catch (error) {
        console.error("Error playing video:", error);
      }
    }

    return () => {
      if (ref) {
        ref.pause();
      }
    };
  }, [activeSlide, isInView, items]);

  return (
    <S.Container>
      {items.length > 1 && (
        <div
          onMouseEnter={() => setIsHoveringTabs(true)}
          onMouseLeave={restartSlideAutoplay}
        >
          <S.StyledScrollWrapper
            direction="horizontal"
            align="left"
            scrollStepType="scrollScreen"
            draggable
            arrowColor="#808893"
            fillType="gradient"
            fillColor="#fff"
            gradientSize={160}
            gap={12}
            activeIndex={items.findIndex(({ id }) => id === activeSlide?.id)}
            items={items.map((item) => ({
              id: String(item.id),
              key: item.id,
              render: () => (
                <S.AccessoryTab
                  key={`tab-${item.id}`}
                  $isActive={activeSlide.id === item.id}
                  onClick={() => handleTabClick(item)}
                >
                  <S.PreviewImg
                    loading="lazy"
                    decoding="async"
                    src={getOptimizedImageUrl(
                      absoluteAssetUrl(item.previewImg),
                      128,
                    )}
                    srcSet={`${getOptimizedImageUrl(absoluteAssetUrl(item.previewImg), 256)} 2x`}
                    alt={t(item.title)}
                  />
                </S.AccessoryTab>
              ),
            }))}
          />
        </div>
      )}

      <S.ContainerWrapper>
        {items.map((item, index) => (
          <S.AddonContainer
            key={`slide-${item.id}`}
            $isActive={activeSlide.id === item.id}
            $canAnimate={hasAnimatedOnce}
            className="slide"
          >
            <S.ContentBlock>
              <VideoCardTags
                tags={
                  item.shouldDisplayTagNames
                    ? item.tags
                    : [{ id: "category", label: t(item.category) }]
                }
                title={item.title}
                isDarker
                visibleTagsCount={
                  item.shouldDisplayTagNames ? 4 : item.defaultTagsVisibleCount
                }
              />

              <S.AddonTitle
                dangerouslySetInnerHTML={{ __html: t(item.title) }}
              />
              <S.Divider />
              <S.AddonDescription>{t(item.description)}</S.AddonDescription>

              {item.dimensions ? (
                <DimensionsBox dimensions={item.dimensions} />
              ) : null}

              {!!getLearnMoreLink(item) && (
                <S.LearnMoreLink
                  href={getLearnMoreLink(item).href}
                  target={item.uiComLink?.href ? "_self" : "_blank"}
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                >
                  <span>{getLearnMoreLink(item).label}</span>
                  <ArrowRightPrimaryIcon size={18} />
                </S.LearnMoreLink>
              )}
            </S.ContentBlock>

            <S.VideoWrapper>
              <ProgressiveBlurGradient
                variant="light"
                isRounded
                maxWidth="428px"
                customGradientColor="#f9f9fa"
              />
              <S.VideoBackground
                ref={(element) => (videoRef.current[index] = element)}
                src={item.video.src}
                poster={item.video.poster}
                preload="none"
                muted
                playsInline
                onEnded={() => autoSwitchNextSlide(item, index)}
              />
            </S.VideoWrapper>
          </S.AddonContainer>
        ))}
      </S.ContainerWrapper>
    </S.Container>
  );
};
